// COBRAR EN EFECTIVO SIN SEÑAL · DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §4.8).
//
// LA VENTA OCURRIÓ: el cliente se fue con la mercancía y el dinero entró.
// Lo que esta cola garantiza es que TEGO se entere exactamente una vez.
// La `clave_idempotencia` nace en `colaBase` ANTES del primer intento y se
// repite en cada reenvío; el servidor devuelve la misma venta a la misma
// clave (`routers/ventas.py`). Por eso aquí ya no se genera ninguna clave
// en la pantalla: dos sitios que generan claves son dos ventas posibles.
//
// SOLO EFECTIVO Y SIN PEDIDO. La tarjeta necesita la confirmación del
// banco en línea; un pedido solo es cobrable si el servidor lo dice. Las
// dos reglas se comprueban AQUÍ, antes de guardar (cinturón), y el
// servidor las vuelve a comprobar con 422 (tirantes).
//
// EL CANDADO ES DE LA PANTALLA. El ajuste de empresa `cobrar_sin_senal`
// decide si la caja ofrece cobrar sin señal; el servidor acepta la venta
// igual y la deja marcada (D-5). Aquí el ajuste solo decide una cosa: si
// una venta que se intentó EN LÍNEA y se quedó sin respuesta puede quedar
// guardada (sí, con el ajuste encendido) o tiene que soltarse con el error
// de red de siempre (ajuste apagado: no se deja una venta encolada en una
// empresa que no lo permitió).
import { api } from '../api';
import {
  crearCola, cargaDe, noLlego, ErrorAlmacen, AVISO_SIN_RESPALDO, PENDIENTE, POR_REVISAR, ENVIADA,
} from './colaBase';
import { importesDeVenta, cambioDe, faltaTratoFiscal } from './importesVenta';

export const EVENTO_VENTA_ENVIADA = 'tego:venta-enviada';

/**
 * La venta como la verá el ticket, calculada en el teléfono con el MISMO
 * orden y el MISMO redondeo por renglón que el servidor (`importesVenta.js`,
 * corrección P1-1 de AUDITORIA-F2). `pendiente: true` y `folio: null` son lo
 * que distingue este papel del definitivo.
 */
export function ventaLocalDe(elemento, {
  nombres = {}, config = {}, cajero = null, tratos = null,
} = {}) {
  const c = elemento.cuerpo;
  // Cómo se grava cada producto (S-1): lo que trajo la copia del catálogo al
  // armar el carrito, guardado junto a la venta. Sin él, una sola tasa.
  const porSku = tratos || elemento.tratos || {};
  const conTrato = (c.renglones || []).map((r) => (porSku[r.sku] ? { ...r, trato_iva: porSku[r.sku] } : r));
  const imp = importesDeVenta(conTrato, c.descuento || null, config);
  const renglones = (c.renglones || []).map((r, i) => ({
    sku: r.sku, producto: nombres[r.sku] || r.sku, cantidad: Number(r.cantidad),
    precio_unitario: Number(r.precio_unitario), importe: imp.renglones[i].importe, ubicacion: r.ubicacion,
  }));
  const { subtotal, impuestos, total, descuento_total: descuentoTotal } = imp;
  const efectivo = c.efectivo_recibido != null ? Number(c.efectivo_recibido) : null;
  return {
    id: null, folio: null, pendiente: true, clave_idempotencia: elemento.clave_idempotencia,
    estado: 'PENDIENTE_DE_ENVIAR', fecha: c.ocurrido_en, ocurrido_en: c.ocurrido_en, sin_senal: true,
    cajero, sucursal: null, cliente_nombre: c.cliente_nombre ?? null,
    subtotal, descuento_total: descuentoTotal, impuestos, total, metodo_pago: c.metodo_pago,
    efectivo_recibido: efectivo,
    cambio: efectivo != null ? cambioDe(c.efectivo_recibido, total) : null,
    renglones,
    guardada_en: new Date(elemento._ts).toISOString(),
  };
}

function resumen(cuerpo, extra) {
  const renglones = cuerpo.renglones || [];
  return {
    total: extra.total ?? null,
    renglones: renglones.length,
    unidades: renglones.reduce((s, r) => s + Number(r.cantidad || 0), 0),
    metodo_pago: cuerpo.metodo_pago,
    nombres: extra.nombres || {},
    ubicacion: renglones[0]?.ubicacion ?? null,
    descuentoTotal: cuerpo.descuento ?? null,
  };
}

// P2-2 (decisión del integrador): el PIN de quien autoriza un descuento NO se
// guarda en el teléfono. La venta se guarda sin él; el primer intento en
// línea sí lo lleva, desde la memoria y solo durante ese intento. Si la venta
// se queda guardada y el servidor exige la autorización al reenviarla, queda
// «por revisar» con su motivo (un 403 no se reintenta, `colaBase.clasificar`).
const pinesEnVuelo = new Map();

function sinPinDescuento(d) {
  if (!d || typeof d !== 'object' || !('pin_autorizacion' in d)) return d;
  const { pin_autorizacion: _pin, ...resto } = d;
  return resto;
}

/** El cuerpo de la venta sin ningún PIN de autorización. */
export function sinPin(cuerpo) {
  const salida = { ...cuerpo };
  if (Array.isArray(cuerpo.renglones)) {
    salida.renglones = cuerpo.renglones.map((r) => (r.descuento ? { ...r, descuento: sinPinDescuento(r.descuento) } : r));
  }
  if (cuerpo.descuento) salida.descuento = sinPinDescuento(cuerpo.descuento);
  return salida;
}

const cola = crearCola({
  db: 'tego-ventas',
  store: 'ventas',
  resumen,
  enviar: (e) => {
    const conPin = pinesEnVuelo.get(e.clave_idempotencia);
    return api.post('/ventas', cargaDe(conPin ? { ...e, cuerpo: { ...e.cuerpo, ...conPin } } : e));
  },
  alEnviada: (e, venta) => ({ folio: venta?.folio ?? null, id_venta: venta?.id ?? null, venta }),
});

function comprobarQuePudoOcurrirSinSenal(cuerpo) {
  if (cuerpo.metodo_pago !== 'EFECTIVO') {
    throw new Error('Sin señal solo se cobra en efectivo: la tarjeta necesita la confirmación del banco en línea. Cobra en efectivo o espera a tener señal.');
  }
  if (cuerpo.folio_pedido) {
    throw new Error('Este pedido solo se puede cobrar con señal: hay que comprobar que sigue vigente. Cóbralo cuando vuelva la señal.');
  }
}

// S-1: sin señal, el ticket solo se imprime si el teléfono sabe cómo se grava
// cada producto. `tratos` es el mapa sku → trato_iva que la caja armó con la
// copia del catálogo; si falta uno, no se cobra sin señal.
function comprobarTratos(cuerpo, tratos) {
  if (!tratos) return;
  const falta = faltaTratoFiscal((cuerpo.renglones || []).map((r) => ({ ...r, trato_iva: tratos[r.sku] })));
  if (falta) {
    throw new Error(`Sin señal no se puede cobrar ${falta.sku}: este teléfono no tiene guardado cómo se grava (IVA) y el ticket saldría con otro total. Cóbralo cuando vuelva la señal.`);
  }
}

// S-3: un descuento mayor que su importe no se guarda sin señal (el
// servidor lo rechazaría y el ticket ya impreso diría otra cosa).
function comprobarDescuentos(cuerpo) {
  const { excedido } = importesDeVenta(cuerpo.renglones || [], cuerpo.descuento || null, {});
  if (excedido) {
    const donde = excedido.renglon === 'TOTAL' ? 'al total' : `en ${cuerpo.renglones[excedido.renglon]?.sku}`;
    throw new Error(`El descuento ${donde} ($${excedido.descuento.toFixed(2)}) es mayor que su importe ($${excedido.importe.toFixed(2)}). Cámbialo o quítalo para poder cobrar.`);
  }
}

const ventana = () => globalThis.window ?? globalThis;

function anunciarEnviada(elemento, venta) {
  try {
    ventana().dispatchEvent?.(new CustomEvent(EVENTO_VENTA_ENVIADA, {
      detail: { clave_idempotencia: elemento.clave_idempotencia, folio: venta?.folio ?? null, venta },
    }));
  } catch { /* sin ventana */ }
}

/**
 * Registra una venta. Devuelve
 *   {estado:'aplicado', venta}                — TEGO ya la tiene, con folio
 *   {estado:'encolado', elemento, ventaLocal} — guardada en este teléfono
 * Lanza si el servidor la rechaza por regla, o si no puede quedar guardada.
 *
 * `sinSenal`: la pantalla está sin señal (se guarda con `sin_senal: true` y
 * la hora del teléfono, sin intentar).
 * `cobrarSinSenal`: el ajuste de empresa; decide si una venta en línea que
 * se quedó sin respuesta puede quedar guardada.
 */
export async function registrarVenta(idUsuario, cuerpo, {
  sinSenal = false, cobrarSinSenal = false, nombres = {}, config = {}, total = null, cajero = null,
  tratos = null,
} = {}) {
  const extra = { nombres, total, ...(tratos ? { tratos } : {}) };
  const guardable = sinPin(cuerpo);
  if (sinSenal || globalThis.navigator?.onLine === false) {
    comprobarQuePudoOcurrirSinSenal(cuerpo);
    comprobarTratos(cuerpo, tratos);
    comprobarDescuentos(cuerpo);
    // Si el almacén falla, `guardar` lanza un ErrorAlmacen que ya dice qué
    // pasó y qué hacer (P1-3): sin señal no hay otro sitio donde dejarla.
    const e = await cola.guardar(idUsuario, {
      ...guardable, sin_senal: true, ocurrido_en: new Date().toISOString(),
    }, extra);
    return { estado: 'encolado', elemento: e, ventaLocal: ventaLocalDe(e, { nombres, config, cajero }) };
  }

  let e;
  try {
    e = await cola.guardar(idUsuario, guardable, extra);
  } catch (err) {
    if (!(err instanceof ErrorAlmacen)) throw err;
    // P1-3: CON señal, un almacén lleno o bloqueado no impide cobrar. Se
    // cobra en línea, sin copia en el teléfono, y se dice.
    const { respuesta } = await cola.enviarSinCopia(idUsuario, cuerpo, extra);
    return { estado: 'aplicado', venta: respuesta, sinRespaldo: true, aviso: AVISO_SIN_RESPALDO };
  }
  // El PIN viaja en el primer intento y solo en él.
  if (JSON.stringify(guardable) !== JSON.stringify(cuerpo)) {
    pinesEnVuelo.set(e.clave_idempotencia, { renglones: cuerpo.renglones, descuento: cuerpo.descuento });
  }
  let r;
  try {
    r = await cola.intentar(e, { enviada: false });
  } finally {
    pinesEnVuelo.delete(e.clave_idempotencia);
  }
  if (r.estado === 'aplicado') return { estado: 'aplicado', venta: r.respuesta };
  if (r.estado === 'por_revisar') {
    await cola.descartar(e.clave_idempotencia);
    throw r.error;
  }
  // Se quedó sin respuesta a mitad del cobro. Solo si la empresa permite
  // cobrar sin señal (y la venta pudo ocurrir sin ella) se queda guardada.
  let puedeQuedar = cobrarSinSenal === true && noLlego(r.error);
  if (puedeQuedar) {
    try { comprobarQuePudoOcurrirSinSenal(cuerpo); comprobarTratos(cuerpo, tratos); } catch { puedeQuedar = false; }
  }
  if (!puedeQuedar) {
    await cola.descartar(e.clave_idempotencia);
    throw r.error;
  }
  // La MISMA clave: si la petición sí llegó y lo que se perdió fue la
  // respuesta, el reenvío devuelve esa venta en vez de cobrar otra.
  const final = await cola.actualizar(e.clave_idempotencia, {
    cuerpo: { ...guardable, sin_senal: true, ocurrido_en: new Date(e._ts).toISOString() },
  });
  return { estado: 'encolado', elemento: final, ventaLocal: ventaLocalDe(final, { nombres, config, cajero }) };
}

/** Reenvía lo pendiente de esta persona; anuncia cada venta enviada. */
export async function sincronizarVentas(idUsuario) {
  const antes = new Set((await cola.listar(idUsuario, { estado: ENVIADA })).map((e) => e.clave_idempotencia));
  const r = await cola.reintentar(idUsuario);
  for (const e of await cola.listar(idUsuario, { estado: ENVIADA })) {
    if (!antes.has(e.clave_idempotencia)) anunciarEnviada(e, e.venta);
  }
  return r;
}

export async function enviarVentaAhora(clave) {
  const r = await cola.enviarAhora(clave);
  const e = await cola.leer(clave);
  if (e?.estado === ENVIADA) anunciarEnviada(e, e.venta);
  return r;
}

export const listarVentas = (idUsuario, opciones) => cola.listar(idUsuario, opciones);
export const leerVenta = (clave) => cola.leer(clave);
export const contarPendientesVenta = (idUsuario) => cola.contar(idUsuario);
export const contarVentasPorRevisar = (idUsuario) => cola.contarPorRevisar(idUsuario);
export const contarVentasDeOtraCuenta = (idUsuario) => cola.contarDeOtraCuenta(idUsuario);
export const descartarVenta = (clave) => cola.descartar(clave);
export const suscribirVentas = (fn) => cola.suscribir(fn);

/**
 * Lo que hace falta para volver a armar el carrito desde una venta guardada
 * («Abrir en Punto de venta»): la persona la cobra de nuevo con señal, con
 * OTRA clave, y la vieja se descarta al confirmar.
 */
export async function carritoDeVentaGuardada(clave) {
  const e = await cola.leer(clave);
  if (!e) return null;
  const nombres = e.resumen?.nombres || {};
  return {
    clave,
    carrito: (e.cuerpo.renglones || []).map((r) => ({
      sku: r.sku, nombre: nombres[r.sku] || r.sku, cantidad: Number(r.cantidad),
      precio: Number(r.precio_unitario), sinPrecio: !(Number(r.precio_unitario) > 0),
      ...(e.tratos?.[r.sku] ? { trato_iva: e.tratos[r.sku] } : {}),
      descuento: r.descuento || null,
    })),
    descuentoTotal: e.cuerpo.descuento || null,
    ubicacion: e.cuerpo.renglones?.[0]?.ubicacion || '',
    motivo: e.motivo || null,
  };
}

export { PENDIENTE, POR_REVISAR, ENVIADA };
